import React, { useState, useEffect, useRef, useMemo } from "react";

const DEVICE_PRESETS = {
  Desktop: { width: 1920, height: 1080, type: "desktop", label: "Desktop FHD (1920×1080)" },
  "MacBook Pro 16": { width: 1728, height: 1117, type: "desktop", label: "MacBook Pro 16\" (1728×1117)" },
  "MacBook Air 13": { width: 1440, height: 900, type: "desktop", label: "MacBook Air 13\" (1440×900)" },
  Laptop: { width: 1366, height: 768, type: "desktop", label: "Standard Laptop (1366×768)" },
  "iPad Pro 12.9": { width: 1024, height: 1366, type: "tablet", label: "iPad Pro 12.9\" (1024×1366)" },
  "iPad Air": { width: 820, height: 1180, type: "tablet", label: "iPad Air (820×1180)" },
  "iPad Mini": { width: 768, height: 1024, type: "tablet", label: "iPad Mini (768×1024)" },
  "Galaxy Tab S9": { width: 800, height: 1280, type: "tablet", label: "Galaxy Tab S9 (800×1280)" },
  "iPhone 15 Pro Max": { width: 430, height: 932, type: "mobile", label: "iPhone 15 Pro Max (430×932)" },
  "iPhone 15 / 14 Pro": { width: 393, height: 852, type: "mobile", label: "iPhone 15 / 14 Pro (393×852)" },
  "iPhone 14 / 13": { width: 390, height: 844, type: "mobile", label: "iPhone 14 / 13 (390×844)" },
  "iPhone SE": { width: 375, height: 667, type: "mobile", label: "iPhone SE (375×667)" },
  "Galaxy S24 Ultra": { width: 412, height: 915, type: "mobile", label: "Galaxy S24 Ultra (412×915)" },
  "Pixel 8": { width: 412, height: 892, type: "mobile", label: "Pixel 8 (412×892)" },
  Custom: { width: 1200, height: 800, type: "custom", label: "Custom Freeform" }
};

const ZOOM_MODES = [
  { value: "fit", label: "Fit to Window" },
  { value: "fitWidth", label: "Fit Width" },
  { value: "fitHeight", label: "Fit Height" },
  { value: "50", label: "50%" },
  { value: "75", label: "75%" },
  { value: "90", label: "90%" },
  { value: "100", label: "100% (Actual)" },
  { value: "125", label: "125%" },
  { value: "150", label: "150%" },
  { value: "200", label: "200%" }
];

const BREAKPOINTS = [
  { width: 320, label: "Mobile S", icon: "" },
  { width: 375, label: "Mobile M", icon: "" },
  { width: 425, label: "Mobile L", icon: "" },
  { width: 768, label: "Tablet", icon: "📟" },
  { width: 1024, label: "Tablet L", icon: "" },
  { width: 1200, label: "Laptop", icon: "" },
  { width: 1440, label: "Desktop", icon: "🖥️" },
  { width: 1920, label: "Full HD", icon: "🖥️" }
];

const filterStyles = {
  none: "none",
  grayscale: "grayscale(100%)",
  protanopia: "url(#protanopia)",
  deuteranopia: "url(#deuteranopia)",
  tritanopia: "url(#tritanopia)"
};

// Calculate common aspect ratio string
function getAspectRatio(w, h) {
  if (!w || !h) return "16:9";
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(w, h);
  const rw = w / divisor;
  const rh = h / divisor;
  if (rw > 30 || rh > 30) {
    const ratio = (w / h).toFixed(2);
    return `${ratio}:1`;
  }
  return `${rw}:${rh}`;
}

export default function ResponsiveSimulator() {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const inputRef = useRef(null);

  // 1. Load preferences from LocalStorage
  const loadPreferences = () => {
    try {
      const stored = localStorage.getItem("qintra_preview_pref") || localStorage.getItem("rabbit_preview_pref");
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to load simulator preferences", e);
    }
    return {};
  };

  const prefs = loadPreferences();

  const defaultTestUrl = typeof window !== "undefined"
    ? `${window.location.origin}/#/dashboard`
    : "http://localhost:3000/#/dashboard";

  // 2. Component states
  const [url, setUrl] = useState(prefs.url || defaultTestUrl);
  const [inputUrl, setInputUrl] = useState(prefs.url || defaultTestUrl);
  const [device, setDevice] = useState(prefs.device || "Desktop");
  const [width, setWidth] = useState(prefs.width || 1920);
  const [height, setHeight] = useState(prefs.height || 1080);
  const [zoomMode, setZoomMode] = useState(prefs.zoomMode || "fit");
  const [visionFilter, setVisionFilter] = useState("none");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [canvasTheme, setCanvasTheme] = useState("dots"); // dots, dark, clean
  const [deviceCategory, setDeviceCategory] = useState("all"); // all, desktop, tablet, mobile

  // Outer workspace dimensions
  const [containerWidth, setContainerWidth] = useState(1200);
  const [containerHeight, setContainerHeight] = useState(800);

  // Reload trigger
  const [iframeKey, setIframeKey] = useState(0);
  const [isReloading, setIsReloading] = useState(false);

  // Save state preferences on change
  useEffect(() => {
    try {
      localStorage.setItem(
        "qintra_preview_pref",
        JSON.stringify({ url, device, width, height, zoomMode })
      );
    } catch (e) {
      console.error("Failed to save preferences", e);
    }
  }, [url, device, width, height, zoomMode]);

  // Handle ResizeObserver to automatically scale
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const { width: cntWidth, height: cntHeight } = entries[0].contentRect;
      setContainerWidth(Math.max(200, cntWidth - 60));
      setContainerHeight(Math.max(200, cntHeight - 60));
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Sync fullscreen class on main app element
  useEffect(() => {
    const appEl = document.querySelector(".app");
    if (appEl) {
      if (isFullScreen) {
        appEl.classList.add("fullscreen-active");
      } else {
        appEl.classList.remove("fullscreen-active");
      }
    }
    return () => {
      if (appEl) {
        appEl.classList.remove("fullscreen-active");
      }
    };
  }, [isFullScreen]);

  // Handle Device changes
  const handleDeviceChange = (selectedKey) => {
    setDevice(selectedKey);
    const preset = DEVICE_PRESETS[selectedKey];
    if (preset) {
      setWidth(preset.width);
      setHeight(preset.height);
    }
  };

  // Swap width and height for screen rotation
  const handleRotate = () => {
    setDevice("Custom");
    const newW = height;
    const newH = width;
    setWidth(newW);
    setHeight(newH);
  };

  // Snap to specific breakpoint
  const handleSnapBreakpoint = (bpWidth) => {
    setDevice("Custom");
    setWidth(bpWidth);
    if (bpWidth <= 430) {
      setHeight(844);
    } else if (bpWidth <= 1024) {
      setHeight(768);
    } else {
      setHeight(1080);
    }
  };

  // Detect current device type for framing
  const currentDeviceType = useMemo(() => {
    if (device !== "Custom" && DEVICE_PRESETS[device]) {
      return DEVICE_PRESETS[device].type;
    }
    if (width <= 500) return "mobile";
    if (width <= 1024) return "tablet";
    return "desktop";
  }, [device, width]);

  // Calculate current zoom factor
  let calculatedScale = 1;
  const frameChromeHeight = currentDeviceType === "desktop" ? 34 : 0;
  const targetTotalWidth = width;
  const targetTotalHeight = height + frameChromeHeight;

  if (zoomMode === "fit") {
    calculatedScale = Math.min(containerWidth / targetTotalWidth, containerHeight / targetTotalHeight);
  } else if (zoomMode === "fitWidth") {
    calculatedScale = containerWidth / targetTotalWidth;
  } else if (zoomMode === "fitHeight") {
    calculatedScale = containerHeight / targetTotalHeight;
  } else {
    calculatedScale = parseFloat(zoomMode) / 100;
  }

  // Bound scale safely between 20% and 300%
  calculatedScale = Math.max(0.2, Math.min(calculatedScale, 3.0));
  const zoomPercentText = Math.round(calculatedScale * 100);

  // Zoom helpers
  const handleZoomIn = () => {
    setZoomMode((prev) => {
      const base = (prev === "fit" || prev === "fitWidth" || prev === "fitHeight") ? calculatedScale : parseFloat(prev) / 100;
      const nextVal = Math.round((base + 0.1) * 10) * 10;
      return String(Math.min(nextVal, 200));
    });
  };

  const handleZoomOut = () => {
    setZoomMode((prev) => {
      const base = (prev === "fit" || prev === "fitWidth" || prev === "fitHeight") ? calculatedScale : parseFloat(prev) / 100;
      const nextVal = Math.round((base - 0.1) * 10) * 10;
      return String(Math.max(nextVal, 30));
    });
  };

  const handleResetZoom = () => {
    setZoomMode("100");
  };

  const handleFitWindow = () => {
    setZoomMode("fit");
  };

  // Resize handle dragging (compensating for scale factor)
  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    setDevice("Custom");
    const startWidth = width;
    const startHeight = height;
    const startX = e.clientX;
    const startY = e.clientY;

    const doDrag = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (direction === "right" || direction === "both") {
        const newWidth = Math.round(startWidth + deltaX / calculatedScale);
        setWidth(Math.max(240, Math.min(newWidth, 3840)));
      }
      if (direction === "bottom" || direction === "both") {
        const newHeight = Math.round(startHeight + deltaY / calculatedScale);
        setHeight(Math.max(240, Math.min(newHeight, 2160)));
      }
    };

    const stopDrag = () => {
      document.removeEventListener("mousemove", doDrag);
      document.removeEventListener("mouseup", stopDrag);
    };

    document.addEventListener("mousemove", doDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  // URL form submission
  const handleUrlSubmit = (e) => {
    e.preventDefault();
    let target = inputUrl.trim();
    if (!target) return;
    if (!/^https?:\/\//i.test(target) && !target.startsWith("/") && !target.startsWith("#")) {
      target = "http://" + target;
    }
    setUrl(target);
    setInputUrl(target);
  };

  // Frame Reload
  const handleReload = () => {
    setIsReloading(true);
    setIframeKey((prev) => prev + 1);
    setTimeout(() => setIsReloading(false), 500);
  };

  // Open in new tab
  const handleOpenExternal = () => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const filteredPresets = useMemo(() => {
    return Object.entries(DEVICE_PRESETS).filter(([key, preset]) => {
      if (deviceCategory === "all") return true;
      if (key === "Custom") return true;
      return preset.type === deviceCategory;
    });
  }, [deviceCategory]);

  return (
    <div className="simulator-layout">
      {/* 1. TOP SIMULATOR TOOLBAR */}
      <div className="simulator-toolbar">
        {/* Navigation buttons: Reload & Home */}
        <div className="toolbar-nav-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleReload}
            title="Reload Preview"
            style={{ padding: "0 10px" }}
          >
            <svg
              viewBox="0 0 24 24"
              width="15"
              height="15"
              stroke="currentColor"
              strokeWidth="2.5"
              fill="none"
              style={{
                transform: isReloading ? "rotate(360deg)" : "none",
                transition: isReloading ? "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)" : "none"
              }}
            >
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => {
              setUrl(defaultTestUrl);
              setInputUrl(defaultTestUrl);
            }}
            title="Home Dashboard"
            style={{ padding: "0 10px" }}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2.5" fill="none">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </button>
        </div>

        {/* Dedicated URL input form with prominent address bar and clear button */}
        <form onSubmit={handleUrlSubmit} className="simulator-url-form">
          <div className="simulator-url-wrapper">
            <span className="simulator-url-icon" title="Secure HTTP/HTTPS Viewport">
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </span>
            <input
              ref={inputRef}
              type="text"
              className="simulator-url-input"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="Enter URL to test (e.g. http://localhost:3000)..."
              title="Target URL to test in responsive simulator"
            />
            {inputUrl && inputUrl.length > 0 && (
              <button
                type="button"
                className="simulator-url-clear-btn"
                onClick={() => {
                  setInputUrl("");
                  if (inputRef.current) inputRef.current.focus();
                }}
                title="Clear URL"
              >
                ✕
              </button>
            )}
          </div>

          <button type="submit" className="toolbar-btn primary" title="Load target URL in simulator">
            Test View
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={handleOpenExternal}
            title="Open tested URL in new browser window"
            style={{ padding: "0 10px" }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
        </form>

        <div className="toolbar-divider"></div>

        {/* Device presets category & selector */}
        <div className="toolbar-control-group">
          <label className="toolbar-label">Type</label>
          <select
            className="toolbar-select"
            value={deviceCategory}
            onChange={(e) => setDeviceCategory(e.target.value)}
            style={{ minWidth: "115px" }}
          >
            <option value="all">All Types</option>
            <option value="desktop">🖥️ Desktop</option>
            <option value="tablet">📟 Tablet</option>
            <option value="mobile"> Mobile</option>
          </select>
        </div>

        <div className="toolbar-control-group">
          <label className="toolbar-label">Device</label>
          <select
            className="toolbar-select"
            value={device}
            onChange={(e) => handleDeviceChange(e.target.value)}
            style={{ minWidth: "160px" }}
          >
            {filteredPresets.map(([key, preset]) => (
              <option key={key} value={key}>
                {preset.label || key}
              </option>
            ))}
          </select>
        </div>

        {/* Viewport size input & orientation toggle */}
        <div className="toolbar-control-group">
          <input
            type="number"
            className="toolbar-num-input"
            value={width}
            onChange={(e) => {
              setDevice("Custom");
              setWidth(Math.max(100, parseInt(e.target.value, 10) || 0));
            }}
            placeholder="W"
            title="Width in pixels"
          />
          <span className="toolbar-x">×</span>
          <input
            type="number"
            className="toolbar-num-input"
            value={height}
            onChange={(e) => {
              setDevice("Custom");
              setHeight(Math.max(100, parseInt(e.target.value, 10) || 0));
            }}
            placeholder="H"
            title="Height in pixels"
          />
          <button
            type="button"
            className="toolbar-btn rotate-btn"
            onClick={handleRotate}
            title="Rotate Orientation (Portrait / Landscape)"
            style={{ padding: "0 8px" }}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2.5" fill="none">
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"></path>
            </svg>
          </button>
        </div>

        <div className="toolbar-divider"></div>

        {/* Vision accessibility filters */}
        <div className="toolbar-control-group">
          <label className="toolbar-label">Vision</label>
          <select
            className="toolbar-select vision-select"
            value={visionFilter}
            onChange={(e) => setVisionFilter(e.target.value)}
          >
            <option value="none">Normal View</option>
            <option value="grayscale">Grayscale (Contrast)</option>
            <option value="protanopia">Protanopia (Red-Blind)</option>
            <option value="deuteranopia">Deuteranopia (Green-Blind)</option>
            <option value="tritanopia">Tritanopia (Blue-Blind)</option>
          </select>
        </div>

        <div className="toolbar-divider"></div>

        {/* Zoom controls */}
        <div className="toolbar-control-group">
          <label className="toolbar-label">Zoom</label>
          <select
            className="toolbar-select zoom-select"
            value={zoomMode}
            onChange={(e) => setZoomMode(e.target.value)}
          >
            {ZOOM_MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </div>

        <div className="toolbar-zoom-buttons">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleZoomOut}
            title="Zoom Out"
            disabled={calculatedScale <= 0.2}
          >
            -
          </button>
          <span className="zoom-display">{zoomPercentText}%</span>
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleZoomIn}
            title="Zoom In"
            disabled={calculatedScale >= 3.0}
          >
            +
          </button>
        </div>

        <button
          type="button"
          className="toolbar-btn"
          onClick={handleFitWindow}
          title="Auto-Fit Preview to Screen"
          style={{ padding: "0 10px" }}
        >
          Fit
        </button>

        <button
          type="button"
          className="toolbar-btn"
          onClick={handleResetZoom}
          title="Actual Size (100%)"
          style={{ padding: "0 10px" }}
        >
          100%
        </button>

        <div className="toolbar-divider"></div>

        {/* Canvas Theme Selector */}
        <button
          type="button"
          className={`toolbar-btn ${canvasTheme === "dark" ? "active" : ""}`}
          onClick={() => setCanvasTheme(canvasTheme === "dots" ? "dark" : canvasTheme === "dark" ? "clean" : "dots")}
          title={`Switch Canvas Background (Current: ${canvasTheme})`}
          style={{ padding: "0 10px" }}
        >
          {canvasTheme === "dark" ? "🌙 Dark" : canvasTheme === "dots" ? "📐 Grid" : "☀️ Light"}
        </button>

        {/* Fullscreen Button */}
        <button
          type="button"
          className={`toolbar-btn ${isFullScreen ? "active" : ""}`}
          onClick={() => setIsFullScreen(!isFullScreen)}
          title={isFullScreen ? "Exit Fullscreen" : "Fullscreen Preview"}
          style={{ padding: "0 10px" }}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2.5" fill="none">
            {isFullScreen ? (
              <path d="M4 14h6v6m10-6h-6v6M4 10h6V4m10 6h-6V4" />
            ) : (
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18-5h-3a2 2 0 0 0-2 2v3m0 11v3a2 2 0 0 0 2 2h3m-18-5v3a2 2 0 0 0 2 2h3" />
            )}
          </svg>
        </button>
      </div>

      {/* 2. BREAKPOINT QUICK-SNAP BAR */}
      <div className="simulator-ruler">
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: "4px" }}>
          Breakpoints:
        </span>
        {BREAKPOINTS.map((bp) => {
          const isActive = width === bp.width;
          return (
            <button
              key={bp.width}
              type="button"
              className={`breakpoint-pill ${isActive ? "active" : ""}`}
              onClick={() => handleSnapBreakpoint(bp.width)}
              title={`Snap Viewport to ${bp.label} (${bp.width}px)`}
            >
              <span>{bp.icon}</span>
              <span className="bp-width">{bp.width}px</span>
              <span className="bp-label">({bp.label})</span>
            </button>
          );
        })}
      </div>

      {/* 3. MAIN PREVIEW CANVAS WORKSPACE */}
      <div
        ref={containerRef}
        className={`simulator-canvas-container theme-${canvasTheme}`}
      >
        {/* Scaled frame wrapper with realistic device shells */}
        <div
          className={`simulator-frame-wrapper frame-${currentDeviceType}`}
          style={{
            width: `${width * calculatedScale}px`,
            height: `${(height + frameChromeHeight) * calculatedScale}px`,
            position: "relative"
          }}
        >
          {/* Mobile Shell Dynamic Island */}
          {currentDeviceType === "mobile" && (
            <div className="mobile-island-bar">
              <div className="mobile-camera-dot"></div>
            </div>
          )}

          {/* Tablet Shell Camera Notch */}
          {currentDeviceType === "tablet" && (
            <div className="tablet-camera-dot"></div>
          )}

          {/* Desktop Browser Chrome Bar */}
          {currentDeviceType === "desktop" && (
            <div
              className="desktop-chrome-bar"
              style={{
                height: `${frameChromeHeight * calculatedScale}px`,
                transformOrigin: "top left"
              }}
            >
              <div className="mac-traffic-lights">
                <span className="mac-dot red"></span>
                <span className="mac-dot yellow"></span>
                <span className="mac-dot green"></span>
              </div>
              <div className="desktop-chrome-title">
                <span style={{ fontSize: "10px" }}></span>
                <span>{url.replace(/^https?:\/\//i, "")}</span>
              </div>
              <span className="desktop-chrome-badge">
                {width} × {height}
              </span>
            </div>
          )}

          {/* Active embedded application frame */}
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={url}
            title="Qintra Responsive Simulator Preview"
            className="simulator-iframe"
            style={{
              width: `${width}px`,
              height: `${height}px`,
              transform: `scale(${calculatedScale})`,
              transformOrigin: "top left",
              filter: filterStyles[visionFilter]
            }}
          />

          {/* Interactive Resize Drag Handles */}
          <div
            className="sim-resize-handle right"
            onMouseDown={(e) => handleResizeStart(e, "right")}
            title="Drag horizontally to resize width"
          ></div>
          <div
            className="sim-resize-handle bottom"
            onMouseDown={(e) => handleResizeStart(e, "bottom")}
            title="Drag vertically to resize height"
          ></div>
          <div
            className="sim-resize-handle corner"
            onMouseDown={(e) => handleResizeStart(e, "both")}
            title="Drag to resize width and height freely"
          ></div>
        </div>
      </div>

      {/* 4. MEDICAL ACCESSIBILITY SVG FILTERS */}
      <svg style={{ display: "none" }}>
        <defs>
          <filter id="protanopia">
            <feColorMatrix
              type="matrix"
              values="0.567 0.433 0.000 0.000 0.000
                      0.558 0.442 0.000 0.000 0.000
                      0.000 0.242 0.758 0.000 0.000
                      0.000 0.000 0.000 1.000 0.000"
            />
          </filter>
          <filter id="deuteranopia">
            <feColorMatrix
              type="matrix"
              values="0.625 0.375 0.000 0.000 0.000
                      0.700 0.300 0.000 0.000 0.000
                      0.000 0.300 0.700 0.000 0.000
                      0.000 0.000 0.000 1.000 0.000"
            />
          </filter>
          <filter id="tritanopia">
            <feColorMatrix
              type="matrix"
              values="0.950 0.050 0.000 0.000 0.000
                      0.000 0.433 0.567 0.000 0.000
                      0.000 0.475 0.525 0.000 0.000
                      0.000 0.000 0.000 1.000 0.000"
            />
          </filter>
        </defs>
      </svg>

      {/* 5. SIMULATOR LIVE TELEMETRY STATUS BAR */}
      <div className="simulator-status-bar">
        <div className="status-left-group">
          <div className="status-item">
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#10b981", display: "inline-block" }}></span>
            <span className="status-label">Device:</span>
            <span className="status-value">{device}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Viewport:</span>
            <span className="status-badge">{width} × {height}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Ratio:</span>
            <span className="status-badge">{getAspectRatio(width, height)}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Orientation:</span>
            <span className="status-value">{width >= height ? "Landscape" : "Portrait"}</span>
          </div>
        </div>

        <div className="status-right-group">
          <div className="status-item">
            <span className="status-label">Vision:</span>
            <span className="status-value" style={{ textTransform: "capitalize" }}>
              {visionFilter === "none" ? "Normal" : visionFilter}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Scale:</span>
            <span className="status-badge">{calculatedScale.toFixed(2)} ({zoomPercentText}%)</span>
          </div>
          <div className="status-item">
            <span className="status-label">Mode:</span>
            <span className="status-value" style={{ textTransform: "capitalize" }}>
              {zoomMode === "fit" ? "Fit Window" : zoomMode === "fitWidth" ? "Fit Width" : zoomMode === "fitHeight" ? "Fit Height" : "Manual"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
